import { DeviceMode, isNodeV2 } from '@vedalib/editor'
import { generateId } from '@vedalib/editor/lib/utils/id'
import * as R from 'ramda'

import { getParentNode } from 'components/Editor/EditorContext/selectors/block'
import { notify } from 'components/uiKit/Notification'
import { NotificationType } from 'components/uiKit/Notification/types'
import { setNavbar, setProjectNavigation, updateBlock } from 'services/Store/Project/actions'
import { NavigationEnum } from 'services/Store/Project/enums'
import { addNode, moveNode } from 'services/Store/Project/mutators/node'
import {
  getBlock,
  getDeviceMode,
  getSelectedNodeIds,
  mergeNodeStyle,
} from 'services/Store/Project/selectors'
import type { Block } from 'services/Store/Project/types'
import store from 'services/Store/store'
import { t } from 'services/Translation'

import { getGroupArea, getGroupCoords } from '../DisplayProvider/utils'
import { genNode } from '../constants'
import { safeParseArea, saveArea } from '../formControls/Area/utils'
import { safeParseCoords, saveCoords } from '../formControls/Coords/utils'

export const onWrapContainer = async () => {
  const state = store.getState().project
  const dispatch = store.dispatch
  const block = getBlock(state)
  const device = getDeviceMode(state)
  const isDesktop = device === DeviceMode.desktop
  const selectedNodes = getSelectedNodeIds(state)
  const children = selectedNodes.map((id) => block?.schema.nodes[id]).filter(isNodeV2)
  const parent = getParentNode(selectedNodes?.[0], block)
  const parentStyle = mergeNodeStyle(parent?.style || {}, device)

  if (block && parent) {
    if (!isDesktop) {
      return notify({
        type: NotificationType.info,
        message: t('editorV2.notifyStructureChangesRestricted'),
      })
    }
    const ordered = R.sortBy((n) => {
      const style = mergeNodeStyle(n.style || {}, device)
      return parentStyle.display === 'grid'
        ? safeParseArea(style?.gridArea).rowStart
        : (safeParseCoords(style.coords).top ?? Infinity)
    }, children).reverse()
    const { rowChanges, colChanges } = ordered.reduce(
      (acc, node) => {
        const area = safeParseArea(node.style?.[device]?.gridArea)
        if (area.rowStart !== acc.prevRowStart) {
          acc.rowChanges += 1
        }
        if (area.colStart !== acc.prevColStart) {
          acc.colChanges += 1
        }
        acc.prevRowStart = area.rowStart
        acc.prevColStart = area.colStart
        return acc
      },
      { rowChanges: 0, colChanges: 0, prevRowStart: -1, prevColStart: -1 },
    )
    const dir = rowChanges >= colChanges ? 'column' : 'row'
    const area = getGroupArea(children, device)
    const { top, left } = getGroupCoords(parent, children, device)
    const index = Math.min(...ordered.map((node) => parent.children.indexOf(node.id)))
    const flex = genNode(generateId(), {
      style: {
        [DeviceMode.desktop]: {
          display: 'flex',
          flexDirection: dir,
          gridArea: saveArea(area),
          flexAlignItems: 'center',
          flexJustifyContent: 'center',
          coords: saveCoords({ top, left }),
        },
      },
    })
    const cbs = ordered.map((node) => moveNode(node.id, flex.id, node.id, null)) as [
      (block: Block) => Block,
    ]
    dispatch(setNavbar({ tab: NavigationEnum.block }))
    dispatch(
      updateBlock({
        id: block.uuid,
        value: R.compose(...cbs, addNode(parent.id, flex, null, { index }))(block),
      }),
    )
    if (ordered.length > 1) {
      dispatch(setProjectNavigation({ nodeIds: [flex.id] }))
    }
  }
}
